import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateDumpMutation } from '../../../../hooks/mutations/useDumpMutation';
import { useDumpsQuery } from '../../../../hooks/queries/useDumpsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { Dump } from '../../../../services/apiSLTP/materials/dumps';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getStatusColor } from '../../../../utils/colors';
import { crudStatus, getCrudStatusInfos, localStorageKeys, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import IconLine from '../../../IconLine';
import UpdateDumpModal from '../../../modals/admin/material/UpdateDumpModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../shared/IconButton';
import GenericAgGridTable from '../../GenericAgGridTable';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/dates';

interface DumpsSettingsRowProps {
    withUsageCount: boolean;
}

const DumpsSettingsRow: React.FC<DumpsSettingsRowProps> = ({ withUsageCount }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedDump, setSelectedDump] = useState<Dump | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const { data: dumps, isLoading: isDumpsLoading, refetch: refetchDumps } = useDumpsQuery({ queries: { withUsage: withUsageCount } });

    const {
        mutate: mutateDump,
        isSuccess: isSuccessDump,
        isError: isErrorDump,
        isLoading: isLoadingDump,
        error: errorDump,
    } = useUpdateDumpMutation({
        onSuccess: () => refetchDumps(),
        onError: (error) => console.error('Error updating dump:', error.message),
    });

    const columns: ColDef<Dump>[] = [
        { headerName: capitalizeFirstLetter(t('name')), field: 'designation', initialFlex: 1, minWidth: 400, colId: 'name' },
        { headerName: capitalizeFirstLetter(t('codeProC')), field: 'codeProC', colId: 'codeProC', initialWidth: 150 },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<Dump>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyDump'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedDump(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatus.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevels.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteDump'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedDump(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatus.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusInfos(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<Dump>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getStatusColor(params.data?.crudStatus)} text={t(getCrudStatusInfos(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('volume') + ` (${t('m³')})`),
            field: 'volume',
            colId: 'volume',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('displayOrder')),
            field: 'displayOrder',
            colId: 'displayOrder',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        createDateTimeColumn<Dump>('createdAt', 'createdAt'),
        createDateTimeColumn<Dump>('updatedAt', 'updatedAt'),
        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: 'xtotal',
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                      initialWidth: 250,
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xTotal')),
                      field: 'usageCount.total',
                      colId: 'xtotal',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xRidesPlanned')),
                      field: 'usageCount.dailyTrucksPlanned',
                      colId: 'xridesplanned',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xRidesCompleted')),
                      field: 'usageCount.dailyTrucksRides',
                      colId: 'xridescompleted',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
              ]
            : []) as ColDef<Dump>[]),
    ];

    const sortedDumps = useMemo(() => {
        return dumps?.sort((a, b) => a.designation.localeCompare(b.designation)) || [];
    }, [dumps]);

    return (
        <>
            <GenericAgGridTable
                columnDefs={columns}
                rowData={sortedDumps}
                loading={isDumpsLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedDump(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addDump'))}
                    />
                }
                exportCsvFileName="admin-dumps"
                storageKey={localStorageKeys.TABLE.ADMIN.MATERIAL.DUMPS}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateDumpModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    dump={selectedDump}
                    isEditMode={isEditMode}
                    refetch={refetchDumps}
                />
            )}
            {showDeleteModal && selectedDump && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateDump({ dumpId: selectedDump._id, data: { crudStatus: crudStatus.DELETED } });
                    }}
                    title={t('dump')}
                    itemName={selectedDump.designation}
                />
            )}
        </>
    );
};

export default DumpsSettingsRow;
